import { set } from 'lodash'

import BaseApi from '@/api/Base/BaseApi'
import {
  DeleteEdaColumnPayload,
  EdaNodeInfo,
  EdaResult,
  ProcessEdaColumnPayload,
  TabularSummary,
  TabularSummaryPreview,
  EdaMetaDataResponse,
  RemoveOutliersPayload,
  GetTabularResultItemsResponse,
  GetWindowItemsPayload,
  StatType,
  TabularRegressionStat,
  TabularRegressionResultSummary,
  UpdateErrorThresholdPayload,
  ProcessEDAMultiColumnPayload
} from '@/api/Tabular/types'
import { CommonListPayload } from '@/api/Node/types'

const ROOT = '/tabular'

const defaultPaginationOptions = {
  itemsPerPage: 40,
  page: 0,
  sortDir: 'asc',
  sortKey: '_id',
}

export default class TabularApi extends BaseApi {
  getTabularSummary(tabularSummaryId: string) {
    return this.agent.get<TabularSummary>(`${ROOT}/summary/${tabularSummaryId}`)
  }

  getTabularSummaryPreview(tabularSummaryId: string) {
    return this.agent.get<TabularSummaryPreview>(`${ROOT}/summary/${tabularSummaryId}/preview`)
  }

  getEdaMetadata(edaMetadataId: string) {
    return this.agent.get<EdaMetaDataResponse>(`${ROOT}/eda-metadata/${edaMetadataId}`)
  }

  getEDANode(edaNodeId: string) {
    return this.agent.get<EdaNodeInfo>(`${ROOT}/eda/${edaNodeId}`)
  }

  processEDA(tabularSummaryId: string, edaNodeId: string) {
    return this.agent.post<EdaNodeInfo>(`${ROOT}/summary/${tabularSummaryId}/eda/${edaNodeId}`)
  }

  getEDAResult(edaResultId: string) {
    return this.agent.get<EdaResult>(`${ROOT}/eda-result/${edaResultId}`)
  }

  resetEDAResult(tabularSummaryId: string, edaNodeId: string) {
    return this.agent.post<EdaNodeInfo>(`${ROOT}/summary/${tabularSummaryId}/eda/${edaNodeId}/reset`)
  }

  deleteEDAColumn(payload: DeleteEdaColumnPayload) {
    const { tabularSummaryId, edaNodeId, ...rest } = payload
    return this.agent.delete<EdaNodeInfo>(`${ROOT}/summary/${tabularSummaryId}/eda/${edaNodeId}/column`, { data: rest })
  }

  processEDAColumn(payload: ProcessEdaColumnPayload) {
    const { tabularSummaryId, edaNodeId, ...rest } = payload
    return this.agent.post(`${ROOT}/summary/${tabularSummaryId}/eda/${edaNodeId}/column`, rest)
  }

  processEDAMultiColumn(payload: ProcessEDAMultiColumnPayload) {
    const { tabularSummaryId, edaNodeId, ...rest } = payload
    return this.agent.post(`${ROOT}/summary/${tabularSummaryId}/eda/${edaNodeId}/multi-column`, rest)
  }

  removeOutliers(payload: RemoveOutliersPayload) {
    const { tabularSummaryId, edaNodeId, ...rest } = payload
    return this.agent.post(`${ROOT}/summary/${tabularSummaryId}/eda/${edaNodeId}/remove-outliers`, rest)
  }

  /** 결과 통합 API */
  getTabularResultItems(nodeId: string, options?: CommonListPayload & { isOutOfThreshold?: boolean}) {
    const { itemsPerPage, page, sortDir, sortKey } = options ?? defaultPaginationOptions
    const params = {
      page,
      num_per_page: itemsPerPage,
      sort_key: sortKey,
      sort_dir: sortDir,
    }

    if (options?.isOutOfThreshold) {
      set(params, 'isOutOfThreshold', options.isOutOfThreshold)
    }
    return this.agent.get<GetTabularResultItemsResponse>(`${ROOT}/${nodeId}/tabular-result-items`, {
      params,
    })
  }

  getWindowItems(payload: GetWindowItemsPayload) {
    const { nodeId, ...rest } = payload
    return this.agent.get<any>(`${ROOT}/${nodeId}/window-items`, { params: rest })
  }

  exportCSV(edaResultId: string) {
    return this.agent.get<any>(`${ROOT}/eda-result/${edaResultId}/export-correlation`, {
      responseType: 'blob',
    })
  }

  getTabularRegressionStats(nodeId: string, statType: StatType) {
    return this.agent.get<TabularRegressionStat[]>(`${ROOT}/${nodeId}/regression-stats`, {
      params: { statType },
    })
  }

  getTabularRegressionResultSummary(nodeId: string) {
    return this.agent.get<TabularRegressionResultSummary>(`${ROOT}/${nodeId}/regression-result-summary`)
  }

  exportRegressionInferenceResult(nodeId: string) {
    return this.agent.get<any>(`${ROOT}/${nodeId}/export-regression-inference`, {
      responseType: 'blob',
    })
  }

  updateErrorThreshold(payload: UpdateErrorThresholdPayload) {
    return this.agent.put<TabularRegressionResultSummary>(`${ROOT}/error-threshold`, payload)
  }
}
